import { useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2 } from 'lucide-react';
import { useI18n } from '../i18n';
import type { UseInpaintingReturn } from '../hooks/useInpainting';

interface InpaintPromptPanelProps {
  inpainting: UseInpaintingReturn;
  /** Screen-space position to anchor the panel near the selection */
  anchorX: number;
  anchorY: number;
}

export default function InpaintPromptPanel({
  inpainting,
  anchorX,
  anchorY,
}: InpaintPromptPanelProps) {
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when panel appears
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const hasPreview = inpainting.previewUrl !== null;
  const isGenerating = inpainting.generating;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isGenerating && inpainting.prompt.trim()) {
      e.preventDefault();
      if (hasPreview) {
        void inpainting.regenerate();
      } else {
        void inpainting.generate();
      }
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      inpainting.cancel();
    }
    // Stop propagation to prevent canvas shortcuts
    e.stopPropagation();
  };

  return (
    <div
      className="fixed z-[var(--z-panel)] bg-popover border border-border rounded-lg shadow-lg p-3 flex flex-col gap-2"
      style={{
        left: anchorX,
        top: anchorY,
        minWidth: 280,
        maxWidth: 360,
      }}
      data-testid="inpaint-prompt-panel"
      data-component="InpaintPromptPanel"
      onPointerDown={(e) => e.stopPropagation()}
    >
      <Input
        ref={inputRef}
        value={inpainting.prompt}
        onChange={(e) => inpainting.setPrompt(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={t('inpaint.promptPlaceholder')}
        disabled={isGenerating}
        data-testid="inpaint-prompt-input"
      />

      <div className="flex gap-2 justify-end">
        {isGenerating ? (
          <Button variant="outline" size="sm" disabled>
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            {t('inpaint.generating')}
          </Button>
        ) : hasPreview ? (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={inpainting.cancel}
              data-testid="inpaint-cancel"
            >
              {t('inpaint.cancel')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void inpainting.regenerate()}
              disabled={!inpainting.prompt.trim()}
              data-testid="inpaint-regenerate"
            >
              {t('inpaint.regenerate')}
            </Button>
            <Button
              size="sm"
              onClick={inpainting.accept}
              data-testid="inpaint-accept"
            >
              {t('inpaint.accept')}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={inpainting.cancel}
              data-testid="inpaint-cancel"
            >
              {t('inpaint.cancel')}
            </Button>
            <Button
              size="sm"
              onClick={() => void inpainting.generate()}
              disabled={!inpainting.prompt.trim()}
              data-testid="inpaint-generate"
            >
              {t('inpaint.generate')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
